import * as React from "react";

import {
    ColumnBuilder,
    ColumnOptions,
    FormattedView,
    CellClassName,
    SimpleColumn,
    sortColumns,
    toColumn,
    buildStandardTools,
    buildToolbarWithViewAdjuster,
    getVisibleColumnsIds,
    StandardToolSpec,
} from "@impero-com/common/components/dataTable";
import { Toolbar } from "@impero-com/common/components/toolbar";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { assertNever } from "@impero-com/common/utils/obj";
import { RowClickHandler } from "@impero-com/common/utils/rowClickHandler";
import { differenceBy, range } from "lodash-es";

import { DEFAULT_COLUMN_BUILDER_ID } from "components/dataViewer";
import { EmptyToggleButton, ToggleButton } from "components/toggleButton";

import { Column, FixedWidthColumn, MinWidthColumn } from "../table";

import * as theme from "./theme.css";

const DEFAULT_PADDING_LEFT = 40;

interface SortConfiguration {
    columnId: string;
    direction: DV.SortDirection;
    onSortChanged: (columnId: string, direction: DV.SortDirection) => void;
}

/**
 * A multi-tree widget, parametrized over a `TreeNode` type. Has a notion of
 * 'active' (currently selected) node. The trees are displayed as a table. The
 * "main" cell is the leftmost one, and holds the collapse control. It is
 * offset to the right depending on the depth of the node. Additional cells may
 * be displayed.
 *
 * The widget also has provision for a whole-row widget for adding a new tree,
 * as well as a whole-row widget for adding rows within a tree.
 *
 * The widget needs a column definition for the main column, and a list of
 * columns for the additional columns. A column instance be `MultiTreeColumn`
 * instance, with a `label` (for the header) field, a `getData(node) -> React
 * component` method for getting the data to display, and an optional `width`
 * field for fixed-with elements.
 *
 * Props:
 * - `trees` (array of objects, mandatory): a list of top-level trees to be displayed.
 * - `mainColumn` (object, mandatory): a column definition for the main column.
 * - `otherColumns` (array of objects, mandatory): a list of column definitions
 * for the additional columns.
 * - `getId` (function, mandatory): takes a node and returns a string identifier.
 * - `getChildren` (function, mandatory): takes a node and returns an array of children of type `Node`
 * - `rowClickHandler` (RowClickHandler, optional): handles clicking in the tree
 * - `getAddTreeWidget` (function, optional): returns a widget to add a new tree. If not defined, no add tree widget will be shown.
 * - `getAddChildWidget` (function, optional): returns a widget to add a new child to a parent node. If not defined, no add child widget will be shown.
 * - `paddingLeft` (number, optional): the number of pixel by depth level used to offset the main cell to the right.
 */
class MultiTree<TreeNode> extends React.Component<
    MultiTreeProps<TreeNode>,
    MultiTreeState
> {
    constructor(props: MultiTreeProps<TreeNode>) {
        super(props);
        this.state = {
            openNodeIds: new Set(),
            activeNodeId: undefined,
        };
    }

    private getActiveNodeIdFromClickHandler = (
        rowClickHandler:
            | RowClickHandler<DV.NodeDescriptor<TreeNode>>
            | undefined
    ) => {
        return rowClickHandler &&
            rowClickHandler.type === "ActiveRowClickHandler"
            ? rowClickHandler.activeRowId
            : undefined;
    };

    componentWillMount() {
        const {
            formattedView: {
                dataView: { dataSource },
            },
            rowClickHandler,
            openRoots,
            hideRoots,
            lastAddedNodeId,
        } = this.props;
        const newActiveNodeId =
            this.getActiveNodeIdFromClickHandler(rowClickHandler);
        const openNodeIds = new Set<string>();

        if (openRoots) {
            const depth = hideRoots ? 1 : 0;
            DV.forEach(
                dataSource.filtered((n) => n.ancestors.length <= depth).iter(),
                (n) => openNodeIds.add(dataSource.getId(n))
            );
        }

        if (newActiveNodeId !== undefined || lastAddedNodeId !== undefined) {
            DV.forEach(dataSource.iter(), (node) => {
                // TODO: leave early
                const nodeId = dataSource.getId(node);
                if (nodeId === newActiveNodeId || nodeId === lastAddedNodeId) {
                    node.ancestors.forEach((ancestor) =>
                        openNodeIds.add(node.getId(ancestor))
                    );
                }
            });
        }

        this.setState({ openNodeIds, activeNodeId: newActiveNodeId });
    }

    componentWillReceiveProps(nextProps: MultiTreeProps<TreeNode>) {
        const oldOpenNodeIds = this.state.openNodeIds;
        const {
            formattedView: {
                dataView: { dataSource },
            },
            hideRoots,
        } = nextProps;
        let newActiveNodeId = this.getActiveNodeIdFromClickHandler(
            nextProps.rowClickHandler
        );

        const lastAddedNodeId =
            nextProps.lastAddedNodeId !== undefined &&
            nextProps.lastAddedNodeId !== this.props.lastAddedNodeId
                ? nextProps.lastAddedNodeId
                : undefined;

        const newOpenNodeIds = new Set<string>();
        let activeNodeIdFound = false;

        // Rebuild the list of open nodes to ensure only valid nodes are present
        DV.forEach(dataSource.iter(), (node) => {
            const nodeId = dataSource.getId(node);
            if (nodeId === newActiveNodeId) {
                activeNodeIdFound = true;
            }
            if (nodeId === lastAddedNodeId) {
                node.ancestors.forEach((ancestor) =>
                    newOpenNodeIds.add(node.getId(ancestor))
                );
                newOpenNodeIds.add(lastAddedNodeId);
            }
            if (oldOpenNodeIds.has(nodeId)) {
                newOpenNodeIds.add(nodeId);
            }
            // Open hidden roots
            if (hideRoots && node.ancestors.length === 0) {
                newOpenNodeIds.add(nodeId);
            }
        });

        if (!activeNodeIdFound) {
            newActiveNodeId = undefined;
        }
        this.setState({
            openNodeIds: newOpenNodeIds,
            activeNodeId: newActiveNodeId,
        });
    }

    render() {
        const {
            formattedView: {
                dataView: { dataSource },
            },
        } = this.props;
        let maxDepth = 0;
        DV.forEach(dataSource.iter(), (node) => {
            if (node.ancestors.length > maxDepth) {
                maxDepth = node.ancestors.length;
            }
        });
        return (
            <table className={theme.table}>
                {this.buildHeader(maxDepth)}
                {this.buildBody(maxDepth)}
            </table>
        );
    }

    private toStaticCellClassName = <T,>(
        cellClassName: CellClassName<T> | undefined
    ): string | undefined => {
        if (!cellClassName) {
            return undefined;
        }
        switch (cellClassName.type) {
            case "Static": {
                return cellClassName.value;
            }
            case "Dynamic": {
                throw new Error(
                    "DataViewer does not support dynamic cellClassName"
                );
            }
            default: {
                return assertNever(cellClassName);
            }
        }
    };

    private buildHeader(maxDepth: number) {
        const { formattedView } = this.props;
        const cols: Array<React.ReactNode> = [];
        const columns: Array<
            Column<DV.NodeDescriptor<TreeNode>, React.ReactNode, any, any>
        > = formattedView.columns.map((simpleCol) => ({
            name: simpleCol.extractor.id,
            label: simpleCol.extractor.label,
            cellClassName: this.toStaticCellClassName(simpleCol.cellClassName),
            getData: (row: DV.NodeDescriptor<TreeNode>) =>
                simpleCol.getFormattedValue(row),
        }));
        cols.push(this.buildTh(columns[0], maxDepth + 2, 0));
        columns.slice(1).forEach((col, index) => {
            cols.push(this.buildTh(col, 1, index + 1));
        });
        return (
            <thead>
                <tr>{cols}</tr>
            </thead>
        );
    }

    private buildTh(
        col: Column<DV.NodeDescriptor<TreeNode>, React.ReactNode, any, any>,
        colSpan: number,
        index: number
    ) {
        const { sortConfiguration } = this.props;
        let sortDirection: DV.SortDirection | undefined;
        let onSortChanged:
            | ((columnId: string, direction: DV.SortDirection) => void)
            | undefined;
        if (sortConfiguration) {
            if (col.name === sortConfiguration.columnId) {
                // TODO: Port sortConfiguration
                sortDirection = sortConfiguration.direction;
            } else {
                sortDirection = undefined;
            }
            onSortChanged = sortConfiguration.onSortChanged;
        } else {
            sortDirection = undefined;
            onSortChanged = undefined;
        }
        let style: React.CSSProperties | undefined;
        if (
            (
                col as MinWidthColumn<
                    DV.NodeDescriptor<TreeNode>,
                    React.ReactNode,
                    any,
                    any
                >
            ).minWidth !== undefined
        ) {
            style = {
                minWidth: (
                    col as MinWidthColumn<
                        DV.NodeDescriptor<TreeNode>,
                        React.ReactNode,
                        any,
                        any
                    >
                ).minWidth,
            };
        } else if (
            (
                col as FixedWidthColumn<
                    DV.NodeDescriptor<TreeNode>,
                    React.ReactNode,
                    any,
                    any
                >
            ).fixedWidth !== undefined
        ) {
            style = {
                width: (
                    col as FixedWidthColumn<
                        DV.NodeDescriptor<TreeNode>,
                        React.ReactNode,
                        any,
                        any
                    >
                ).fixedWidth,
            };
        }
        return (
            <th
                key={index}
                colSpan={colSpan}
                className={`${theme.th} ${
                    onSortChanged ? theme.sortableColumn : ""
                }`}
                style={style}
                onClick={
                    onSortChanged
                        ? () => {
                              let newSortDirection: DV.SortDirection;
                              switch (sortDirection) {
                                  case "ASC":
                                      newSortDirection = "DESC";
                                      break;
                                  case "DESC":
                                      newSortDirection = "ASC";
                                      break;
                                  case undefined:
                                      newSortDirection = "ASC";
                                      break;
                                  default:
                                      return assertNever(sortDirection);
                              }
                              return onSortChanged(col.name, newSortDirection);
                          }
                        : undefined
                }
            >
                {sortDirection
                    ? [
                          <span key="label" className={theme.sortedColumnLabel}>
                              {col.label}
                          </span>,
                          this.buildSortArrow(sortDirection),
                      ]
                    : col.label}
            </th>
        );
    }

    private buildSortArrow(sortDirection: DV.SortDirection) {
        let className: string;
        switch (sortDirection) {
            case "ASC":
                className = theme.arrowDown;
                break;
            case "DESC":
                className = theme.arrowUp;
                break;
            default:
                return assertNever(sortDirection);
        }
        return <div key="arrow" className={className} />;
    }

    private buildBody(maxDepth: number) {
        return (
            <tbody>
                {this.buildTree(maxDepth)}
                {this.buildAddTreeRow(maxDepth)}
            </tbody>
        );
    }

    private buildTree(maxDepth: number) {
        const {
            getAddChildWidget,
            formattedView: {
                dataView: { dataSource },
            },
            hideRoots,
        } = this.props;
        const rows: Array<React.ReactNode> = [];
        const nodesWithChildren = new Set<string>();
        DV.forEach(dataSource.iter(), (node) => {
            if (node.children().length > 0) {
                nodesWithChildren.add(dataSource.getId(node));
            }
        });
        const visibleNodes = DV.toArray(
            dataSource
                .filtered((node) => {
                    return (
                        node.ancestors.length === 0 ||
                        this.state.openNodeIds.has(
                            node.getId(
                                node.ancestors[node.ancestors.length - 1]
                            )
                        )
                    );
                })
                .iter()
        );

        type VisitedNode =
            | {
                  type: "NodeVisited";
                  node: DV.NodeDescriptor<TreeNode>;
                  depth: number;
              }
            | {
                  type: "BranchVisited";
                  parent: { node: TreeNode; id: string };
                  depth: number;
              };

        interface Acc {
            visitedNodes: Array<VisitedNode>;
            lastNode?: DV.NodeDescriptor<TreeNode>;
        }

        const { visitedNodes } = visibleNodes.reduce(
            ({ visitedNodes, lastNode }: Acc, node, index) => {
                if (lastNode) {
                    const diffAncestors = differenceBy(
                        lastNode.ancestors,
                        node.ancestors,
                        node.getId
                    );
                    const isLastNodeOpen = this.state.openNodeIds.has(
                        dataSource.getId(lastNode)
                    );
                    if (
                        (diffAncestors.length !== 0 ||
                            lastNode.children().length === 0) &&
                        isLastNodeOpen
                    ) {
                        visitedNodes.push({
                            type: "BranchVisited",
                            parent: {
                                node: lastNode.value,
                                id: dataSource.getId(lastNode),
                            },
                            depth: lastNode.ancestors.length,
                        });
                    }
                    diffAncestors.reverse().forEach((ancestor, index) => {
                        visitedNodes.push({
                            type: "BranchVisited",
                            parent: {
                                node: ancestor,
                                id: node.getId(ancestor),
                            },
                            depth: lastNode.ancestors.length - index - 1,
                        });
                    });
                }
                visitedNodes.push({
                    type: "NodeVisited",
                    node,
                    depth: node.ancestors.length,
                });
                if (index === visibleNodes.length - 1) {
                    // Last node in list
                    const isNodeOpen = this.state.openNodeIds.has(
                        dataSource.getId(node)
                    );
                    if (isNodeOpen) {
                        visitedNodes.push({
                            type: "BranchVisited",
                            parent: {
                                node: node.value,
                                id: dataSource.getId(node),
                            },
                            depth: node.ancestors.length,
                        });
                    }
                    for (let i = node.ancestors.length - 1; i >= 0; i--) {
                        const ancestor = node.ancestors[i];
                        visitedNodes.push({
                            type: "BranchVisited",
                            parent: {
                                node: ancestor,
                                id: node.getId(ancestor),
                            },
                            depth: i,
                        });
                    }
                }
                return {
                    visitedNodes,
                    lastNode: node,
                };
            },
            {
                visitedNodes: [],
            }
        );

        visitedNodes.forEach((visited) => {
            // This is an array with the content of each TD but not offset to
            // the left (this is done by buildTr())
            let cells: Array<Cell>;
            let nodeId: string;
            let actualDepth: number;
            const depth = hideRoots ? visited.depth - 1 : visited.depth;
            if (depth < 0) {
                return;
            } // Skip hidden roots
            switch (visited.type) {
                case "NodeVisited": {
                    const treeNode = visited.node;
                    nodeId = dataSource.getId(treeNode);
                    cells = this.buildNode(
                        treeNode,
                        depth,
                        !this.state.openNodeIds.has(nodeId),
                        nodesWithChildren.has(nodeId)
                    );
                    actualDepth = depth;
                    break;
                }
                case "BranchVisited": {
                    const { node: parentNode, id: parentId } = visited.parent;
                    // we have looped over all the children of the parent node, add
                    // a widget to add a new row if needed
                    const addChildWidget = getAddChildWidget
                        ? getAddChildWidget(parentNode, depth + 1)
                        : null;
                    if (addChildWidget !== null) {
                        cells = [this.buildAddWidgetCell(addChildWidget)];
                        nodeId = `add-row-${parentId}`;
                        // The "add row" widget counts as a child of the current row
                        actualDepth = depth + 1;
                    } else {
                        // No need to add a row, don't do anything, we'll insert
                        // the previous row next time
                        return;
                    }
                    break;
                }
                default:
                    return assertNever(visited);
            }
            rows.push(this.buildTr(cells, nodeId, actualDepth, maxDepth));
            return;
        });
        return rows;
    }

    private buildTr(
        cells: Array<Cell>,
        id: string,
        depth: number,
        maxDepth: number
    ) {
        const actualPaddingLeft =
            this.props.paddingLeft !== undefined
                ? this.props.paddingLeft
                : DEFAULT_PADDING_LEFT;
        const [mainCell, ...otherCells] = cells;
        // Include the nesting and 1 for the Add widget
        // Extend the main cell over the entire row if we don't have other cells
        // (this is necessary for the "add row" button)
        const columns = this.props.formattedView.columns;
        const colSpan =
            maxDepth -
            depth +
            1 +
            (otherCells.length === 0 ? columns.length + 1 : 1);
        const isSelected = this.state.activeNodeId === id;
        let className = isSelected ? theme.selectedTr : theme.unselectedTr;
        if (this.props.rowClickHandler) {
            className += ` ${theme.clickable}`;
        }
        if (this.props.lastAddedNodeId === id) {
            className += ` ${theme.recentlyAddedNode}`;
        }
        return (
            <tr key={id} className={className}>
                {depth > 0
                    ? // Don't use colspan here, as this causes indent display bugs
                      range(0, depth).map((index) => (
                          <td
                              key={index}
                              className={theme.leftPadder}
                              style={{
                                  // Yes, we need all that for extreme cases.
                                  minWidth: actualPaddingLeft + "px",
                                  width: actualPaddingLeft + "px",
                                  maxWidth: actualPaddingLeft + "px",
                              }}
                          >
                              {"\xa0"}
                          </td>
                      ))
                    : null}
                <td
                    colSpan={colSpan}
                    className={theme.mainTd}
                    onClick={mainCell.onClick}
                >
                    {mainCell.component}
                </td>
                {otherCells.map((cell, index) => (
                    <td
                        key={index}
                        className={theme.otherTd}
                        onClick={cell.onClick}
                    >
                        {cell.component}
                    </td>
                ))}
            </tr>
        );
    }

    private buildNode(
        node: DV.NodeDescriptor<TreeNode>,
        depth: number,
        isCollapsed: boolean,
        hasChildren: boolean
    ) {
        const cells: Array<Cell> = [];
        const columns: Array<
            Column<DV.NodeDescriptor<TreeNode>, React.ReactNode, any, any>
        > = this.props.formattedView.columns.map((simpleCol) => {
            return {
                name: simpleCol.extractor.id,
                label: simpleCol.extractor.label,
                cellClassName: this.toStaticCellClassName(
                    simpleCol.cellClassName
                ),
                getData: (row: DV.NodeDescriptor<TreeNode>) =>
                    simpleCol.getFormattedValue(row),
            };
        });
        cells.push(
            this.buildMainCell(
                columns[0],
                node,
                isCollapsed,
                hasChildren,
                depth
            )
        );
        columns.slice(1).forEach((col) => {
            const data = col.getData(node, node.index);
            // Insert a non-breakable space where there is no data,
            // otherwise the cells are way too small
            cells.push({
                component: (
                    <div>
                        {data !== null && data !== undefined && data !== ""
                            ? data
                            : "\xa0"}
                    </div>
                ),
                onClick: () => this.handleActiveNodeChanged(node),
            });
        });
        return cells;
    }

    private buildMainCell(
        mainColumn: Column<
            DV.NodeDescriptor<TreeNode>,
            React.ReactNode,
            any,
            any
        >,
        node: DV.NodeDescriptor<TreeNode>,
        isCollapsed: boolean,
        hasChildren: boolean,
        depth: number
    ) {
        const {
            hideToggleWidgets,
            getAddChildWidget,
            isOpenAllNode,
            formattedView: {
                dataView: { dataSource },
            },
        } = this.props;
        let toggleButton: React.ReactNode;
        if (!hideToggleWidgets) {
            if (
                hasChildren ||
                (getAddChildWidget &&
                    getAddChildWidget(node.value, depth + 1) !== null)
            ) {
                toggleButton = (
                    <ToggleButton
                        isCollapsed={isCollapsed}
                        onToggle={() => {
                            const newIsCollapsed = !isCollapsed;
                            const { openNodeIds } = this.state;
                            const newOpenNodeIds = new Set<string>(openNodeIds);
                            const nodeId = dataSource.getId(node);
                            if (newIsCollapsed) {
                                newOpenNodeIds.delete(nodeId);
                                DV.forEach(dataSource.iter(), (node) => {
                                    if (
                                        node.ancestors.some(
                                            (ancestor) =>
                                                node.getId(ancestor) === nodeId
                                        )
                                    ) {
                                        newOpenNodeIds.delete(
                                            dataSource.getId(node)
                                        );
                                    }
                                });
                            } else {
                                newOpenNodeIds.add(nodeId);
                            }
                            this.setState({ openNodeIds: newOpenNodeIds });
                        }}
                    />
                );
            } else if (isOpenAllNode && isOpenAllNode(node)) {
                // TODO this is a POC
                toggleButton = (
                    <div
                        style={{
                            display: "block",
                            marginLeft: "auto",
                            marginRight: "auto",
                            width: 25,
                            fontWeight: "bold",
                        }}
                    >
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                this.collapseAll();
                            }}
                        >
                            &lt;
                        </a>{" "}
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                this.expandAll();
                            }}
                        >
                            &gt;
                        </a>
                    </div>
                );
            } else {
                toggleButton = <EmptyToggleButton />;
            }
        } else {
            toggleButton = null;
        }
        const data = mainColumn.getData(node, node.index);
        return {
            component: (
                <div className={theme.mainCell}>
                    <div className={theme.mainCellWidgets}>
                        {toggleButton ? (
                            <div className={theme.toggleButton}>
                                {toggleButton}
                            </div>
                        ) : null}
                    </div>
                    <div
                        className={`${
                            hideToggleWidgets
                                ? theme.mainCellInnerContentNoToggle
                                : theme.mainCellInnerContent
                        }`}
                    >
                        {data !== null && data !== undefined && data !== ""
                            ? data
                            : "\xa0"}
                    </div>
                </div>
            ),
            onClick: () => this.handleActiveNodeChanged(node),
        };
    }

    private buildAddTreeRow(maxDepth: number) {
        const { getAddTreeWidget } = this.props;
        if (getAddTreeWidget) {
            return this.buildTr(
                [this.buildAddWidgetCell(getAddTreeWidget())],
                "add-tree",
                0,
                maxDepth
            );
        } else {
            return null;
        }
    }

    private buildAddWidgetCell(widget: React.ReactNode) {
        const { hideToggleWidgets } = this.props;
        return {
            component: (
                <div className={theme.mainCell}>
                    <div className={theme.mainCellWidgets}>
                        {hideToggleWidgets ? null : (
                            <div className={theme.toggleButton}>
                                <EmptyToggleButton />
                            </div>
                        )}
                    </div>
                    <div
                        className={`${
                            hideToggleWidgets
                                ? theme.mainCellAddWidgetInnerContentNoToggle
                                : theme.mainCellAddWidgetInnerContent
                        }`}
                    >
                        {widget}
                    </div>
                </div>
            ),
            onClick: undefined,
        };
    }

    private handleActiveNodeChanged = (node: DV.NodeDescriptor<TreeNode>) => {
        const {
            formattedView: {
                dataView: { dataSource },
            },
            rowClickHandler,
        } = this.props;
        const nodeId = dataSource.getId(node);
        if (nodeId === this.state.activeNodeId) {
            if (
                rowClickHandler &&
                rowClickHandler.type === "SimpleRowClickHandler" &&
                (!rowClickHandler.isClickEnabled ||
                    rowClickHandler.isClickEnabled(node))
            ) {
                rowClickHandler.onClick(
                    node,
                    /* Navigation is not supported */ undefined
                );
            }
            return;
        }
        this.setState({ activeNodeId: nodeId });
        if (
            rowClickHandler &&
            (!rowClickHandler.isClickEnabled ||
                rowClickHandler.isClickEnabled(node))
        ) {
            rowClickHandler.onClick(
                node,
                /* Navigation is not supported */ undefined
            );
        }
    };

    private expandAll() {
        const {
            formattedView: {
                dataView: { dataSource },
            },
        } = this.props;
        const newOpenNodeIds = new Set<string>();
        DV.forEach(dataSource.iter(), (node) => {
            newOpenNodeIds.add(dataSource.getId(node));
        });
        this.setState({ openNodeIds: newOpenNodeIds });
    }

    private collapseAll() {
        this.setState({ openNodeIds: new Set() });
    }
}

interface Cell {
    component: React.ReactNode;
    onClick: (() => void) | undefined;
}

class MultiTreeWithToolBar<TreeNode> extends React.Component<
    MultiTreeWithToolBarProps<TreeNode>,
    MultiTreeWithToolBarState<TreeNode>
> {
    constructor(props: MultiTreeWithToolBarProps<TreeNode>) {
        super(props);
        const formattedView = buildFormattedView(
            props.dataView,
            props.columnBuilders
        );
        this.state = {
            formattedView,
            visibleColumnIds: props.visibleColumnIds || new Set(),
        };
    }

    static getDerivedStateFromProps = <TreeNode extends object>(
        props: MultiTreeWithToolBarProps<TreeNode>,
        state: MultiTreeWithToolBarState<TreeNode>
    ) => {
        const { visibleColumnIds } = state;
        const { dataView, columnBuilders } = props;
        const formattedView = buildFormattedView(dataView, columnBuilders);
        return {
            formattedView,
            visibleColumnIds: getVisibleColumnsIds(
                formattedView,
                visibleColumnIds
            ),
        };
    };

    render = () => {
        const {
            toolbarClassName,
            floatingToolbar,
            tools: _tools,
            dataView,
            columnBuilders: _cb,
            ...multiTreeProps
        } = this.props;
        const { formattedView, visibleColumnIds } = this.state;
        const columns: Array<SimpleColumn<DV.NodeDescriptor<TreeNode>>> =
            formattedView.columns.filter((simpleCol) =>
                visibleColumnIds.has(simpleCol.extractor.id)
            );

        return (
            <>
                <Toolbar
                    tools={this.buildTools()}
                    toolbarClassName={toolbarClassName}
                    floatingToolbar={floatingToolbar}
                />
                <MultiTree
                    {...multiTreeProps}
                    formattedView={{
                        dataView,
                        columns,
                    }}
                />
            </>
        );
    };

    private buildTools = () => {
        const { formattedView, visibleColumnIds } = this.state;
        const tools: Array<StandardToolSpec<DV.NodeDescriptor<TreeNode>>> =
            this.props.tools || this.getDefaultToolSpecs();
        return buildStandardTools(
            tools,
            formattedView,
            visibleColumnIds,
            this.handleVisibleColumnsChanged
        );
    };

    private getDefaultToolSpecs = (): Array<
        StandardToolSpec<DV.NodeDescriptor<TreeNode>>
    > => {
        return buildToolbarWithViewAdjuster();
    };

    private handleVisibleColumnsChanged = (visibleColumnIds: Set<string>) => {
        this.setState({ visibleColumnIds });
        if (this.props.onVisibleColumnsChanged) {
            this.props.onVisibleColumnsChanged(visibleColumnIds);
        }
    };
}

// FVItem can either be TreeNode or Expando<TreeNode>
function buildFormattedView<FVItem>(
    dataView: DV.DataView<DV.NodeDescriptor<FVItem>>,
    columnBuilders:
        | { [extractorId: string]: ColumnBuilder<any, any> }
        | undefined
) {
    const allColumns = dataView.extractorGroups.reduce(
        (memo, group) => {
            return memo.concat(
                group.extractors.map((extractor) => {
                    const builder =
                        (columnBuilders &&
                            (columnBuilders[extractor.id] ||
                                columnBuilders[DEFAULT_COLUMN_BUILDER_ID])) ||
                        undefined;
                    if (builder) {
                        return toColumn(extractor, builder(extractor));
                    } else {
                        return toColumn(extractor);
                    }
                })
            );
        },
        [] as Array<SimpleColumn<DV.NodeDescriptor<FVItem>>>
    );
    const displayedColumns = allColumns.filter(
        (col) => col.display !== "NEVER"
    );
    sortColumns(displayedColumns);
    const formattedView: FormattedView<DV.NodeDescriptor<FVItem>> = {
        dataView,
        columns: displayedColumns,
    };
    return formattedView;
}

interface MultiTreeProps<TreeNode> {
    formattedView: FormattedView<DV.NodeDescriptor<TreeNode>>;
    hideToggleWidgets?: boolean;
    sortConfiguration?: SortConfiguration;
    rowClickHandler?: RowClickHandler<DV.NodeDescriptor<TreeNode>>;
    getAddTreeWidget?: () => React.ReactNode;
    getAddChildWidget?: (
        parentNode: TreeNode,
        depth: number
    ) => React.ReactNode | null;
    isOpenAllNode?: (node: DV.NodeDescriptor<TreeNode>) => boolean;
    paddingLeft?: number;
    openRoots?: boolean;
    lastAddedNodeId?: string;
    hideRoots?: boolean;
}

// TODO: port expanders ?
interface MultiTreeWithToolBarProps<TreeNode> extends Omit<
    MultiTreeProps<TreeNode>,
    "formattedView"
> {
    id: string;
    dataView: DV.DataView<DV.NodeDescriptor<TreeNode>>;
    // Be careful with column builders, the item may be expanded.
    // That is why ColumnBuilder is typed with any instead of TreeNode/Expando<TreeNode>.
    columnBuilders?: { [extractorId: string]: ColumnBuilder<any, any> };
    toolbarClassName?: string;
    floatingToolbar?: boolean;
    tools?: Array<StandardToolSpec<DV.NodeDescriptor<TreeNode>>>;
    visibleColumnIds?: Set<string>;
    onVisibleColumnsChanged?: (visibleColumnIds: Set<string>) => void;
}

interface MultiTreeState {
    openNodeIds: Set<string>;
    activeNodeId: string | undefined;
}

interface MultiTreeWithToolBarState<TreeNode> {
    formattedView: FormattedView<DV.NodeDescriptor<TreeNode>>;
    visibleColumnIds: Set<string>;
}

interface MultiTreeColumn<TreeNode> {
    id: string;
    label: string;
    getData: (node: DV.NodeDescriptor<TreeNode>) => React.ReactNode;
    width?: number;
    cellClassName?: string;
}

interface DummyData {
    mainValue: string;
    col1: string;
    col2: string;
    children: Array<DummyData>;
}

function DummyMultiTree(props: {
    rowClickHandler?: RowClickHandler<DV.NodeDescriptor<DummyData>>;
}) {
    const tree1: DummyData = {
        mainValue: "1. main this is some very long text here",
        col1: "1. col1",
        col2: "1. col2",
        children: [
            {
                mainValue: "1.1. main",
                col1: "1.1. col1",
                col2: "1.1. col2",
                children: [
                    {
                        mainValue: "1.1.1. main",
                        col1: "1.1.1. col1",
                        col2: "1.1.1. col2",
                        children: [],
                    },
                    {
                        mainValue: "1.1.2. main",
                        col1: "1.1.2. col1",
                        col2: "1.1.2. col2",
                        children: [
                            {
                                mainValue: "1.1.2.1. main",
                                col1: "1.1.2.1. col1",
                                col2: "1.1.2.1. col2",
                                children: [] as Array<DummyData>,
                            },
                        ],
                    },
                    {
                        mainValue: "1.1.3. main",
                        col1: "1.1.3. col1",
                        col2: "1.1.3. col2",
                        children: [] as Array<DummyData>,
                    },
                ],
            },
            {
                mainValue: "1.2. main",
                col1: "1.2. col1",
                col2: "1.2. col2",
                children: [] as Array<DummyData>,
            },
        ],
    };
    const tree2: DummyData = {
        mainValue: "2. main",
        col1: "2. col1",
        col2: "2. col2 this is also pretty long",
        children: [] as Array<DummyData>,
    };
    const trees = [tree1, tree2];
    const getId = (node: DummyData) => node.mainValue;
    const getChildren = (node: DummyData) => node.children;
    const getString = (node: DV.NodeDescriptor<DummyData>) =>
        node.value.mainValue;
    const getCol1 = (node: DV.NodeDescriptor<DummyData>) => node.value.col1;
    const getCol2 = (node: DV.NodeDescriptor<DummyData>) => node.value.col2;

    const stringCompare = Cmp.string();

    const columns: Array<DV.DataExtractor<DV.NodeDescriptor<DummyData>, any>> =
        [
            {
                id: "main",
                label: "Main column bla bla bla bla bla bla",
                getValue: getString,
                asString: getString,
                compare: Cmp.map(getString, stringCompare),
            },
            {
                id: "col1",
                label: "Column 1",
                getValue: getCol1,
                asString: getCol1,
                compare: Cmp.map(getCol1, stringCompare),
            },
            {
                id: "col2",
                label: "Column 2",
                getValue: getCol2,
                asString: getCol2,
                compare: Cmp.map(getCol2, stringCompare),
            },
        ];
    const MultiTreeWithToolBarElt: new (
        props: MultiTreeWithToolBarProps<DummyData>
    ) => React.Component<MultiTreeWithToolBarProps<DummyData>, any> =
        MultiTreeWithToolBar;

    const dataView: DV.DataView<DV.NodeDescriptor<DummyData>> = {
        dataSource: new DV.MultiTreeDataSource(
            trees,
            getChildren,
            getId,
            (node) => node.mainValue
        ),
        extractorGroups: [
            {
                id: "dummy",
                label: "Dummy",
                extractors: columns,
            },
        ],
        sorters: [],
        defaultCompareItems: Cmp.map((node) => node.index),
    };

    return (
        <MultiTreeWithToolBarElt
            id="dummy-multi-tree"
            columnBuilders={{
                col1: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<DummyData>> =>
                    toColumn(extractor, { layout: { fixedWidth: 130 } }),
                col2: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<DummyData>> =>
                    toColumn(extractor, { layout: { fixedWidth: 150 } }),
            }}
            getAddTreeWidget={() => (
                <div style={{ cursor: "pointer", padding: "10px 15px" }}>
                    Add
                </div>
            )}
            getAddChildWidget={(node) => {
                return node.mainValue !== "1.1.3. main" ? (
                    <div style={{ cursor: "pointer", padding: "10px 15px" }}>
                        Add row
                    </div>
                ) : null;
            }}
            rowClickHandler={props.rowClickHandler}
            dataView={dataView}
        />
    );
}

export type {
    MultiTreeProps,
    MultiTreeWithToolBarProps,
    SortConfiguration,
    MultiTreeColumn,
};
export { MultiTree, MultiTreeWithToolBar, DummyMultiTree };
